import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Types
import type {
	I18nCtx,
	CoverTemplate,
	EVChargerTemplate,
	PVSystemTemplate,
	ThermostatTemplate,
	VirtualTemplateType,
} from './virtual_template_types';

export const VIRTUAL_TEMPLATES: {
	thermostat: ThermostatTemplate;
	cover: CoverTemplate;
	valve: CoverTemplate;
	pv_system: PVSystemTemplate;
	ev_charger: EVChargerTemplate;
} = {
	thermostat: {
		type: 'thermostat',
		current_temperature: {
			type: 'number',
			icon: 'far fa-temperature-sun',
			unit: '°C',
			label: (ctx: I18nCtx) =>
				ctx.translate('vc_template_current_temperature', 'Current temperature'),
		},
		target_temperature: {
			type: 'number',
			icon: 'far fa-thermometer-three-quarters',
			unit: '°C',
			label: (ctx: I18nCtx) =>
				ctx.translate('vc_template_target_temperature', 'Target temperature'),
		},
		enable: {
			type: 'boolean',
			icon: 'far fa-toggle-on',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_enable_thermostat', 'Enable thermostat'),
		},
		current_humidity: {
			type: 'number',
			optional: true,
			icon: 'far fa-droplet',
			unit: '%',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_current_humidity', 'Current Humidity'),
		},
		thermostat_mode: {
			type: 'enum',
			optional: true,
			icon: 'fa fa-temperature-list',
			icons: {
				auto: 'fas fa-a',
				cool: 'fas fa-temperature-snow',
				heat: 'fas fa-temperature-sun',
				off: 'fas fa-power-off',
			},
			create_config: {
				options: ['auto', 'cool', 'heat', 'off'],
				default_value: 'auto',
				meta: {
					cloud: [],
					ui: {
						titles: {
							cool: 'Cool',
							heat: 'Heat',
							auto: 'Auto',
							off: 'Off',
						},
						view: 'dropdown',
					},
				},
			},
			label: (ctx: I18nCtx) => ctx.translate('vc_template_thermostat_mode', 'Thermostat mode'),
		},
		fan_mode: {
			type: 'enum',
			optional: true,
			icon: 'far fa-sun',
			icons: {
				auto: 'fas fa-a',
				low: 'fak fa-solid-fan-speed-low',
				medium: 'fak fa-solid-fan-speed-medium',
				high: 'fak fa-solid-fan-speed-high',
				off: 'fas fa-power-off',
			},
			create_config: {
				options: ['auto', 'low', 'medium', 'high', 'off'],
				default_value: 'auto',
				meta: {
					cloud: [],
					ui: {
						titles: {
							auto: 'Auto',
							low: 'Low',
							medium: 'Medium',
							high: 'High',
							off: 'Off',
						},
						view: 'dropdown',
					},
				},
			},
			label: (ctx: I18nCtx) => ctx.translate('vc_template_fan_mode', 'Fan mode'),
		},
	},
	cover: {
		type: 'cover',
		open: {
			type: 'button',
			icon: 'fa fa-arrow-up',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_open', 'Open'),
		},
		close: {
			type: 'button',
			icon: 'fa fa-arrow-down',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_close', 'Close'),
		},
		stop: {
			type: 'button',
			icon: 'fa fa-pause',
			optional: true,
			label: (ctx: I18nCtx) => ctx.translate('vc_template_stop', 'Stop'),
		},
		position: {
			type: 'number',
			optional: true,
			icon: 'fas fa-blinds-raised',
			unit: '%',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_position', 'Position'),
		},
		status: {
			type: 'enum',
			optional: true,
			icon: 'fas fa-info-circle',
			icons: {
				open: 'fas fa-blinds-raised',
				closed: 'fas fa-blinds',
				opening: 'fas fa-blinds-raised',
				closing: 'fas fa-blinds',
				stopped: 'fas fa-hand',
			},
			create_config: {
				options: ['open', 'closed', 'opening', 'closing', 'stopped'],
				default_value: 'closed',
				meta: {
					cloud: [],
					ui: {
						titles: {
							open: 'Open',
							closed: 'Closed',
							opening: 'Opening',
							closing: 'Closing',
							stopped: 'Stopped',
						},
						view: 'dropdown',
					},
				},
			},
			label: (ctx: I18nCtx) => ctx.translate('vc_template_shutter_state', 'Shutter state'),
		},
	},
	valve: {
		type: 'valve',
		open: {
			type: 'button',
			icon: 'fa fa-arrow-up',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_open', 'Open'),
		},
		close: {
			type: 'button',
			icon: 'fa fa-arrow-down',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_close', 'Close'),
		},
		stop: {
			type: 'button',
			icon: 'fa fa-pause',
			optional: true,
			label: (ctx: I18nCtx) => ctx.translate('vc_template_stop', 'Stop'),
		},
		position: {
			type: 'number',
			optional: true,
			icon: 'fas fa-pipe-valve',
			unit: '%',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_position', 'Position'),
		},
		status: {
			type: 'enum',
			optional: true,
			icon: 'fas fa-info-circle',
			icons: {
				open: 'far fa-pipe-valve',
				closed: 'fas fa-pipe-valve',
				opening: 'far fa-pipe-valve',
				closing: 'fas fa-pipe-valve',
				stopped: 'fas fa-hand',
			},
			create_config: {
				options: ['open', 'closed', 'opening', 'closing', 'stopped'],
				default_value: 'closed',
				meta: {
					cloud: [],
					ui: {
						titles: {
							open: 'Open',
							closed: 'Closed',
							opening: 'Opening',
							closing: 'Closing',
							stopped: 'Stopped',
						},
						view: 'dropdown',
					},
				},
			},
			label: (ctx: I18nCtx) => ctx.translate('vc_template_valve_state', 'Valve state'),
		},
	},
	pv_system: {
		type: 'pv_system',
		house_consumption: {
			type: 'number',
			icon: 'fas fa-home',
			unit: 'W',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_home_consumption', 'Home consumption'),
		},
		grid_consumption: {
			type: 'number',
			icon: 'fas fa-utility-pole',
			unit: 'W',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_grid_consumption', 'Grid consumption'),
		},
		pv_consumption: {
			type: 'number',
			icon: 'fas fa-solar-panel',
			unit: 'W',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_pv_consumption', 'PV consumption'),
		},
		battery_consumption: {
			type: 'number',
			optional: true,
			icon: 'fas fa-bolt',
			unit: 'W',
			label: (ctx: I18nCtx) =>
				ctx.translate('vc_template_battery_consumption', 'Battery consumption'),
		},
		battery_level: {
			type: 'number',
			optional: true,
			icon: 'fa-duotone fa-light fa-battery-full',
			unit: '%',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_battery_level', 'Battery level'),
		},
		battery_state: {
			type: 'text',
			optional: true,
			icon: 'fas fa-battery-bolt',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_battery_state', 'Battery state'),
		},
	},
	ev_charger: {
		type: 'ev_charger',
		current: {
			type: 'number',
			unit: 'A',
			icon: 'far fa-bolt-auto',
			label: (ctx: I18nCtx) => ctx.translate('current', 'Current'),
		},
		power: {
			type: 'number',
			unit: 'kW',
			icon: 'fas fa-bolt',
			label: (ctx: I18nCtx) => ctx.translate('power', 'Power'),
		},
		charged: {
			type: 'number',
			unit: 'kW/h',
			icon: 'fas fa-bolt',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_charged', 'Charged'),
		},
		max_charge: {
			type: 'number',
			optional: true,
			unit: 'A',
			icon: 'far fa-bolt-auto',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_max_charge', 'Max charge'),
		},
		total_charge: {
			type: 'number',
			optional: true,
			unit: 'kW',
			icon: 'fas fa-bolt',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_total_charge', 'Total charge'),
		},
		auto_start: {
			type: 'boolean',
			optional: true,
			icon: 'fas fa-bolt',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_auto_start', 'Auto start'),
		},
		auto_balancing: {
			type: 'boolean',
			optional: true,
			icon: 'fas fa-bolt',
			label: (ctx: I18nCtx) => ctx.translate('vc_template_auto_balancing', 'Auto balancing'),
		},
		work_state: {
			type: 'enum',
			optional: true,
			icon: 'fas fa-bolt',
			icons: {
				idle: 'far fa-plug',
				waiting: 'fas fa-hourglass-half',
				paused: 'fas fa-pause',
				charging: 'fas fa-battery-bolt',
				completed: 'fas fa-check',
				error: 'fas fa-exclamation-triangle',
			},
			create_config: {
				options: ['idle', 'waiting', 'paused', 'charging', 'completed', 'error'],
				default_value: 'idle',
				meta: {
					cloud: [],
					ui: {
						titles: {
							idle: 'Idle',
							waiting: 'Waiting',
							paused: 'Paused',
							charging: 'Charging',
							completed: 'Completed',
							error: 'Error',
						},
						view: 'dropdown',
					},
				},
			},
			label: (ctx: I18nCtx) => ctx.translate('vc_template_work_state', 'Work state'),
		},
	},
};

export const getVirtualTemplateList = (): {
	[key: string]: { type: VirtualTemplateType; title: string; description: string; icon: string };
} => {
	const $format = get(format);
	return {
		thermostat: {
			type: 'thermostat',
			title: $format('vc_template_thermostat', { default: 'Thermostat' }),
			description: $format('vc_template_thermostat_desc', { default: 'Heating/cooling control' }),
			icon: 'far fa-thermometer-three-quarters',
		},
		cover: {
			type: 'cover',
			title: $format('vc_template_cover', { default: 'Shutter' }),
			description: $format('vc_template_cover_desc', { default: 'Open/close blinds' }),
			icon: 'far fa-blinds-raised',
		},
		valve: {
			type: 'valve',
			title: $format('vc_template_valve', { default: 'Valve' }),
			description: $format('vc_template_valve_desc', {
				default: 'Open/close valves for automation',
			}),
			icon: 'far fa-pipe-valve',
		},
		pv_system: {
			type: 'pv_system',
			title: $format('vc_template_pv_system', { default: 'Photovoltaic system' }),
			description: $format('vc_template_pv_system_desc', {
				default: 'Monitor production and grid flow',
			}),
			icon: 'far fa-solar-panel',
		},
		ev_charger: {
			type: 'ev_charger',
			title: $format('vc_template_ev_charger', { default: 'EV Charger' }),
			description: $format('vc_template_ev_charger_desc', {
				default: 'Control charging sessions and power',
			}),
			icon: 'fas fa-charging-station',
		},
	};
};

export const buildVCTemplateWizard = () => {
	const $format = get(format),
		wizardStep = [
			{
				title: $format('vc_template_wizard_select_title', {
					default: 'Select Template',
				}),
				subtitle: '',
			},
			{
				title: $format('vc_template_wizard_create_title', {
					default: 'Configure Template',
				}),
				subtitle: '',
			},
			{
				title: $format('vc_template_wizard_preview_title', {
					default: 'Preview',
				}),
				subtitle: '',
			},
		];
	return wizardStep.map((item, i) => {
		return {
			...item,
			active: i === 0 ? true : false,
			stepNumber: i + 1,
			activeIndex: i,
		};
	});
};
